import { Box, Button, FormControl, Stack, Typography } from '@mui/material';
import Modal from '@mui/material/Modal';
import Rails from '@rails/ujs';
import Axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import Select from 'react-select';
import toastr from 'toastr';
import { customerLink, orderLink } from '../Common/functions';
import { ISelectOption, Nullable, ProjectTaskData } from '../Common/types';
import { RoleContext } from './List';
import PriorityField from './PriorityField';
import StatusField from './StatusField';
import TaskDetailsModal from './TaskDetailsModal';

interface TableRowProps {
  isChecked: boolean;
  projectTask: ProjectTaskData;
  allMachines: ISelectOption[];
  onCheckboxClick: (project: ProjectTaskData, checked: boolean) => void;
  onTruckUpdated: () => void;
}

const TaskRow = (props: TableRowProps) => {
  const projectTask = props.projectTask;
  const task_type = projectTask.task_type;

  const roleContext = React.useContext(RoleContext);

  const allowedToChangeMachine =
    roleContext.hasRole('super_admin') ||
    roleContext.hasRole('pm_production_manager_wooden') ||
    roleContext.hasRole('pm_production_manager_windoors') ||
    roleContext.hasRole('pm_packing_manager');

  const isOperatorView =
    roleContext.hasRole('pm_operator') && !roleContext.hasRole('super_admin');

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const [machine, setMachine] = useState<Nullable<ISelectOption>>(
      projectTask.machine
        ? ({
            label: projectTask.machine.name,
            value: projectTask.machine.id.toString(),
          } as ISelectOption)
        : null
    ),
    [operator, setOperator] = useState<Nullable<ISelectOption>>(
      projectTask.operator
        ? ({
            label: projectTask.operator.name,
            value: projectTask.operator.id.toString(),
          } as ISelectOption)
        : null
    ),
    [operators, setOperators] = useState(new Array<ISelectOption>()),
    [open, setOpen] = useState(false),
    [detailsOpen, setDetailsOpen] = useState(false),
    [commentsCount, setCommentsCount] = useState(projectTask.task_comments_count || 0),
    [attachmentsCount, setAttachmentsCount] = useState(projectTask.task_attachments_count || 0);

  const hasNotes = commentsCount > 0 || attachmentsCount > 0;

  function toggleChecked(): void {
    props.onCheckboxClick(props.projectTask, !props.isChecked);
  }

  function updateProjectTask(taskId: number, data: any): Promise<boolean> {
    return Axios.put(
      `/task_management/${taskId}.json`,
      { project_task: data },
      {
        headers: {
          'X-CSRF-Token': Rails.csrfToken(),
          'Content-Type': 'application/json',
        },
      }
    )
      .then(() => true)
      .catch((err) => {
        const errors = err.response && err.response.data;
        const message =
          errors && typeof errors === 'object'
            ? Object.keys(errors).map((field) => `${field} ${errors[field].join(', ')}`).join('; ')
            : err.message;
        toastr.error(message, 'Task is NOT updated');
        console.log(err);
        return false;
      });
  }

  function handleMachineChange(opt: ISelectOption): void {
    const previous = machine;
    setMachine(opt);
    updateProjectTask(projectTask.id, { machine_id: parseInt(opt.value) }).then((ok) => {
      if (!ok) setMachine(previous);
    });
  }

  function handleOperatorChange(opt: ISelectOption): void {
    setOperator(opt);
    updateProjectTask(projectTask.id, { user_id: parseInt(opt.value) });
    setOpen(false);
  }

  function openDetails(e: React.MouseEvent): void {
    e.preventDefault();
    setDetailsOpen(true);
  }

  function handleCountsChange(comments: number, attachments: number): void {
    setCommentsCount(comments);
    setAttachmentsCount(attachments);
  }

  function handleStatusChange(taskId: number, opt: ISelectOption): void {
    updateProjectTask(projectTask.id, { status: opt.value });
  }

  function loadOperators() {
    Axios({ url: `/operatorships.json?machine=${machine.value}` })
      .then((res) => {
        setOperators(
          res.data.map((m) => ({ label: m.name, value: m.user_id.toString() }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }

  useEffect(() => {
    if (machine) {
      loadOperators();
    }
  }, [machine]);

  return (
    <Fragment>
      <tr>
        {true && (
          <td onClick={() => toggleChecked()}>
            <input
              type="checkbox"
              checked={props.isChecked}
              onChange={() => toggleChecked()}
              className="i-checks"
            />
          </td>
        )}
        <td className="text-nowrap">
          <a href="#" onClick={openDetails} title="Task details, comments and files">
            {projectTask.id}
          </a>
          <a href="#" onClick={openDetails} className={`m-l-xs ${hasNotes ? 'text-danger' : 'text-muted'}`} title="Comments and files">
            <i className={`fa fa-comments-o ${hasNotes ? 'fa-lg' : ''}`}></i>
            {commentsCount > 0 && <small> {commentsCount}</small>}
          </a>
          {attachmentsCount > 0 && (
            <a href="#" onClick={openDetails} className="m-l-xs text-danger" title="Files">
              <i className="fa fa-paperclip fa-lg"></i>
              <small> {attachmentsCount}</small>
            </a>
          )}
          {detailsOpen && (
            <TaskDetailsModal
              open={detailsOpen}
              projectTask={projectTask}
              onClose={() => setDetailsOpen(false)}
              onCountsChange={handleCountsChange}
            />
          )}
        </td>
        <td>
          {projectTask.project && !isOperatorView &&
            orderLink(
              projectTask.project.order,
              projectTask.project.order_visible_id
            )}
          {projectTask.project && isOperatorView && projectTask.project.order_visible_id}
        </td>
        <td>
          {projectTask.project && (
            isOperatorView ? (
              projectTask.project.visible_id
            ) : (
              <a href={`/projects/${projectTask.project.id}`}>
                {projectTask.project.visible_id}
              </a>
            )
          )}
        </td>
        <td>
          {projectTask.project && (
            isOperatorView ? (
              projectTask.project.name
            ) : (
              <a href={`/projects/${projectTask.project.id}`}>
                {projectTask.project.name}
              </a>
            )
          )}
        </td>
        <td>{projectTask.project?.impregnation || '-'}</td>
        <td>
          {projectTask.project && projectTask.project.walls_thickness
            ? `${projectTask.project.walls_thickness} mm`
            : '-'}
        </td>
        {!isOperatorView && (
          <td>
            {projectTask.project && customerLink(projectTask.project.customer)}
          </td>
        )}
        <td>{task_type ? task_type.name : ''}</td>

        <td>
          {allowedToChangeMachine && (
            <a href="#" onClick={() => setOpen(true)}>
              {machine ? machine.label : '--empty--'}
            </a>
          )}
          {!allowedToChangeMachine && machine?.label}
        </td>
        <td>
          {allowedToChangeMachine && (
            <a href="#" onClick={() => setOpen(true)}>
              {operator ? operator.label : '--empty--'}
            </a>
          )}
          {!allowedToChangeMachine && operator?.label}
        </td>
        <td className={projectTask.prod_deadline_after_deadline ? "text-danger" : ""}>
          {projectTask.prod_deadline}
          {projectTask.prod_deadline_after_deadline && (
            <i
              className="fa fa-exclamation-triangle"
              title="Production deadline is after deadline"
            ></i>
          )}
        </td>
        <td>
          <PriorityField
            priority={projectTask.priority}
            taskId={projectTask.id}
            updateField={updateProjectTask}
          />
        </td>
        <td>
          <StatusField
            status={projectTask.status}
            taskId={projectTask.id}
            onChange={handleStatusChange}
          />
        </td>
        <td>
          {projectTask.completion_duration}
          <Modal
            open={open}
            onClose={() => setOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography variant="h6" component="h2">
                {allowedToChangeMachine
                  ? 'Assign Machine/Operator'
                  : 'Assign Operator'}
              </Typography>

              <FormControl fullWidth>
                <Stack spacing={2} direction="column">
                  {allowedToChangeMachine && (
                    <div className="wrapper wrapper-content">
                      <Select
                        placeholder="Machine"
                        options={props.allMachines}
                        value={machine}
                        onChange={(val) => handleMachineChange(val)}
                      />
                    </div>
                  )}
                  <div className="wrapper wrapper-content">
                    <Select
                      placeholder="Operator"
                      options={operators}
                      defaultValue={operator}
                      onChange={(val) => handleOperatorChange(val)}
                    />
                  </div>
                  <Stack direction="row" justifyContent="end">
                    <Button onClick={() => setOpen(false)}>Close</Button>
                  </Stack>
                </Stack>
              </FormControl>
            </Box>
          </Modal>
        </td>
        <td>{projectTask.project && projectTask.project.material_m3}</td>
      </tr>
    </Fragment>
  );
};

export default TaskRow;
